/** @returns {'BNB' | 'RENTAL' | null} */
export function normalizeListingScope(raw) {
  if (raw == null || String(raw).trim() === '') return null;
  const upper = String(raw).trim().toUpperCase();
  if (upper === 'BNB' || upper === 'AIRBNB') return 'BNB';
  if (upper === 'RENTAL' || upper === 'RENTALS') return 'RENTAL';
  const lower = String(raw).trim().toLowerCase();
  if (lower === 'bnb') return 'BNB';
  if (lower === 'rental' || lower === 'rentals') return 'RENTAL';
  return null;
}

export function filterUnitsByListingScope(units, scope) {
  if (!scope || !Array.isArray(units)) return units || [];
  if (scope === 'BNB') return units.filter(isBnbUnit);
  if (scope === 'RENTAL') return units.filter((unit) => !isBnbUnit(unit));
  return units;
}

/** True when the API marks a unit as removed (soft or hard delete metadata). */
export function isUnitDeleted(unit) {
  if (!unit || typeof unit !== 'object') return true;
  if (unit.isDeleted === true || unit.deleted === true) return true;
  if (unit.deletedAt != null && String(unit.deletedAt).trim() !== '') return true;
  const status = String(unit.status || unit.unitStatus || '').trim().toUpperCase();
  if (status === 'DELETED' || status === 'INACTIVE' || status === 'REMOVED') return true;
  if (unit.isActive === false) return true;
  return false;
}

/** Units that should appear on tenant/public listing grids. */
export function isUnitListedPublicly(unit) {
  if (!unit || typeof unit !== 'object') return false;
  if (isUnitDeleted(unit)) return false;
  if (unit.isPublic === false) return false;
  return true;
}

/**
 * Resolve rental vs BnB scope for property detail pages when the URL has no scope param.
 * @returns {'BNB' | 'RENTAL'}
 */
export function resolveListingScope(listingScope, { property, units } = {}) {
  const normalized = normalizeListingScope(listingScope);
  if (normalized) return normalized;

  const propScope = String(property?.dashboardScope || '').trim().toUpperCase();
  if (propScope === 'BNB') return 'BNB';
  if (propScope === 'RENTAL') return 'RENTAL';

  const list = Array.isArray(units) ? units : [];
  if (list.length > 0) {
    if (list.every(isBnbUnit)) return 'BNB';
    if (list.every((u) => !isBnbUnit(u))) return 'RENTAL';
    // Mixed property: rental browse should not show BnB units
    return 'RENTAL';
  }

  return 'RENTAL';
}

export function buildPropertyDetailsSearch({ unitId, scope } = {}) {
  const params = new URLSearchParams();
  if (unitId != null && unitId !== '') params.set('unitId', String(unitId));
  const normalizedScope = normalizeListingScope(scope);
  if (normalizedScope === 'BNB') params.set('scope', 'bnb');
  else if (normalizedScope === 'RENTAL') params.set('scope', 'rental');
  return params.toString();
}

/**
 * Detects short-stay / BnB units (aligned with Tenant PropertyDetails.jsx).
 */
export function isBnbUnit(unit) {
  if (!unit || typeof unit !== 'object') return false;
  const hasNightPrice =
    typeof unit.pricePerNight === 'number' &&
    Number.isFinite(unit.pricePerNight) &&
    unit.pricePerNight > 0;
  const hasCheckout = String(unit.checkoutTime || '').trim() !== '';
  return hasNightPrice || hasCheckout;
}

/**
 * True when a public property should only appear under BnB listings, not long-term rental / buy tabs.
 *
 * @param {object} prop - Summary or detailed property from GET /api/public/properties
 * @param {Set<string>} bnbPropertyIds - Property ids returned by GET /api/public/bnb/units
 */
export function isExclusiveBnbPublicProperty(prop, bnbPropertyIds) {
  if (!prop) return false;
  if (String(prop.dashboardScope || '').toUpperCase() === 'BNB') return true;

  const pid = prop.id ?? prop.propertyId;
  const units = Array.isArray(prop.units) ? prop.units : [];

  if (pid != null && pid !== '' && bnbPropertyIds.has(String(pid))) {
    if (units.length === 0) return true;
    return units.every(isBnbUnit);
  }

  if (units.length > 0 && units.every(isBnbUnit)) return true;
  return false;
}
